import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import {
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  getFleetCheckRemainderFrequency,
  updateFleetCheckFrequency,
} from '../../../../services/fleet_service';
import { setAccessTokenSelector } from 'redux/users/ducks';
import { useSelector } from 'react-redux';
import ActionButtons from './ActionButtons';
import { setNotification } from 'redux/notifications/ducks';
import { useDispatch } from 'react-redux';

export const FleetCheck = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fleetCheckFrequency, setFleetCheckFrequency] = useState(0);
  const [parkedDaysFrequency, setParkedDaysFrequency] = useState(0);
  const options = [5, 10, 30, 90];
  const access_token = useSelector(setAccessTokenSelector);
  const dispatch = useDispatch();
  const initialFrequencyRef = useRef(null);
  const initialParkedDaysRef = useRef(null);

  useEffect(() => {
    async function getRemainder() {
      const res = await getFleetCheckRemainderFrequency(access_token);
      if (res.status === 200) {
        const {
          data: {
            resp: { fleetcheck_reminder_days, parked_no_of_days },
          },
        } = res;
        setFleetCheckFrequency(fleetcheck_reminder_days);
        setParkedDaysFrequency(parked_no_of_days);

        initialFrequencyRef.current = fleetcheck_reminder_days;
        initialParkedDaysRef.current = parked_no_of_days;
      }
    }
    getRemainder();
  }, [access_token]);

  const handleUpdate = async () => {
    try {
      setIsSubmitting(true);

      const payload = {};
      if (initialFrequencyRef.current !== fleetCheckFrequency) {
        payload.fleetcheck_reminder_days = fleetCheckFrequency;
      }
      if (initialParkedDaysRef.current !== parkedDaysFrequency) {
        payload.parked_no_of_days = parkedDaysFrequency;
      }

      if (Object.keys(payload).length === 0) {
        setIsSubmitting(false);
        return;
      }

      const res = await updateFleetCheckFrequency(access_token, payload);

      if (res.status === 200) {
        dispatch(
          setNotification(t('fleetCheck.fleetCheckUpdateMsg'), 'success', true)
        );
      }

      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const handleChange = ({ target: { value } }) => {
    if (value <= 365 && value >= 0) {
      setFleetCheckFrequency(value);
    }
  };

  const handleChangeParked = ({ target: { value } }) => {
    if (value <= 90 && value >= 5) {
      setParkedDaysFrequency(value);
    }
  };

  return (
    <>
      <Grid container spacing={2} marginBottom={2}>
        <Grid
          item
          xs={12}
          lg={4}
          style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
        >
          <label
            style={{ fontSize: 18, fontWeight: 13 }}
            data-hook='fleet-check-label'
          >
            {t('customerSettingsPage.fleets.fleetCheckFrequency')}
          </label>
          <TextField
            id='outlined-basic'
            label={t('customerSettingsPage.fleets.selectDays')}
            variant='outlined'
            value={fleetCheckFrequency}
            onChange={handleChange}
            type='number'
            data-hook='fleet-check-days-input'
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} marginBottom={2}>
        <Grid
          item
          xs={12}
          lg={4}
          style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
        >
          <label
            style={{ fontSize: 18, fontWeight: 13 }}
            data-hook='fleet-check-parked-label'
          >
            {t('customerSettingsPage.fleets.parkedDays')}
          </label>

          <FormControl
            size='small'
            sx={{
              minWidth: 200,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'grey',
                  borderWidth: 2,
                  borderRadius: 1,
                },
                '&:hover fieldset': {
                  borderColor: 'primary',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary',
                },
              },
            }}
          >
            <InputLabel
              id='number-select-label'
              sx={{
                color: 'grey',
                fontWeight: 700,
              }}
            >
              {t('customerSettingsPage.fleets.selectDays')}
            </InputLabel>

            <Select
              labelId='number-select-label'
              value={parkedDaysFrequency}
              label={t('customerSettingsPage.fleets.selectDays')}
              onChange={handleChangeParked}
              sx={{ color: 'black' }}
            >
              {options.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <ActionButtons
        handleUpdate={handleUpdate}
        disabled={
          isSubmitting ||
          (initialFrequencyRef.current !== null &&
            initialParkedDaysRef.current !== null &&
            initialFrequencyRef.current === fleetCheckFrequency &&
            initialParkedDaysRef.current === parkedDaysFrequency)
        }
        isSubmitting={isSubmitting}
      />
    </>
  );
};
